import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BasicModal from '../../BasicModal/BasicModal';
import ProductField from '../Transaction/Fields/ProductField';
import ImageField from '../Transaction/Fields/ImageField';
import DividerField from '../Transaction/Fields/DividerField';
import {
  TransactionDetailRow,
  TransactionDetailLabel,
  TransactionDetailValue,
} from '../Transaction/Fields/DetailRow';
import Status, { StatusLabel } from '../../Portfolio/RecentTransactions/Status';
import ModalButtons from '../../Modal/ModalButtons';
import SectionLoader from '../../Loader/SectionLoader';
import { ReactComponent as DollarRoundIcon } from '../../../assets/images/svg/dollar-round.svg';
import {
  transactionStatusTypes,
  transactionStatusTypesArray,
  triggerOrderStatusTypesArray,
} from '../../../util/enum/api/transactionTypes';
import { userSuffixTypes } from '../../../util/enum/api/accountTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import useTriggerOrderVerification from '../../../util/hooks/useTriggerOrderVerification';
import { TARGET_PRICE_AGAIN_STATUS } from '../../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { requestTargetPriceAgain } from '../../../store/actions/triggerOrders/triggerOrdersActions';
import { selectTargetPriceAgain } from '../../../store/selectors/triggerOrdersSelectors';
import { buildProductImageSrc } from '../../GTT/Common/productFilterHelpers';
import config from '../../../config';
import { formatDate } from '../../../util/helpers/dateHelpers';
import { formatAbsolutMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { selectAccountUsersData } from '../../../store/selectors/accountSelectors';
import { uFlexCenter } from '../../../assets/styles/utility';
import { pxToRem } from '../../../assets/styles/helper';
import tokens from '../../../assets/styles/tokens';

const TriggerOrderIconWrap = styled.div`
  ${uFlexCenter};
  margin-top: ${pxToRem(-8)};
  margin-bottom: ${pxToRem(16)};

  svg {
    width: ${pxToRem(40)};
    height: ${pxToRem(40)};
  }
`;

const ButtonWrap = styled.div`
  margin-top: auto;
  padding: 0 ${pxToRem(16)} ${pxToRem(16)};
`;

// Status draws its icon in the table's 16px, and its label in a paragraph of its
// own, which would take the 16px of the global p rule and the table's weight and
// colour. Here the icon is 12px below the modal's mobile breakpoint (768px), and
// the label is given the @gbi/ui body text style (the 200 step, the nearest to
// the other rows' 14px values) and primary text colour.
const FieldRowValue = styled(TransactionDetailValue)`
  ${StatusLabel} {
    ${tokens.type.body[200]}
    color: ${tokens.color.text.primary};
  }
`;

// The BE words some rows' labels itself (a custom caption), in whatever case it
// sends them; every label of the details is shown in title case.
const FieldRowLabel = styled(TransactionDetailLabel)`
  text-transform: capitalize;
`;

const DATE_FORMAT = 'MMMM d, y hh:mm aa';

// A regular transaction carries its Price Per Oz and Price Per Unit as fields; a
// canceled or expired order doesn't, so they are worked out from its target
// price, which is per unit. Price per oz also needs the ounces in a unit, read
// from the start of the product's name ("1 oz ...", "1/10 oz ..."), and is left
// out when the name doesn't give them.
const OUNCES_IN_NAME = /^(\d+(?:\.\d+)?)(?:\/(\d+))?\s*oz\b/i;

const getOuncesPerUnit = (productCaption = '') => {
  const match = productCaption.match(OUNCES_IN_NAME);

  if (!match) {
    return undefined;
  }

  const [, numerator, denominator] = match;
  const ounces = denominator ? numerator / denominator : Number(numerator);

  return ounces > 0 ? ounces : undefined;
};

// The code of the order's product: on its ProductImage field (as in a regular
// transaction's details), failing that on its Product field, failing that on
// the order itself.
const getProductCode = (transaction) => {
  const fields = transaction.Fields || [];

  return (
    fields.find((field) => field.FormatType === 'ProductImage') ||
    fields.find(
      (field) => field.FormatType === 'Product' && field.ProductCode,
    ) ||
    transaction
  ).ProductCode;
};

// The vault the order was placed for, as the details show it: the Location type
// of its Product field (a canceled or expired order's Fields) or, failing that,
// of the order itself, and whether it is a segregated one.
const getOrderVault = (transaction) => {
  const productField = (transaction.Fields || []).find(
    (field) => field.FormatType === 'Product',
  );

  return {
    locationType: productField?.LocationType ?? transaction.LocationType,
    isSegregated: !!transaction.IsSegregated,
  };
};

// The product's image, for the top of the modal. An order with no product code
// has no image.
const getProductImageUrl = (transaction, rootUrl) =>
  encodeURI(
    buildProductImageSrc(
      config.algoliaSearch.imageCode,
      rootUrl,
      getProductCode(transaction),
    ),
  );

// What both variants share: the titled modal and the product image (the dollar
// icon when there isn't one), with the Set target price again button at the
// bottom for an expired order, and a spinner over it all while that button's
// request is being carried out. The modal is closed with its X.
const TriggerOrderModalFrame = ({
  isOpen,
  onClose,
  title,
  imageUrl,
  showSetTargetPriceAgain,
  onSetTargetPriceAgain,
  isLoading,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <BasicModal
      size="md"
      isOpen={isOpen}
      close={onClose}
      title={title}
      dataCy="container-trigger-order-details"
      closeIconVisible
      fixedHeight
    >
      <SectionLoader isLoading={isLoading}>
        {imageUrl ? (
          <ImageField url={imageUrl} />
        ) : (
          <TriggerOrderIconWrap>
            <DollarRoundIcon />
          </TriggerOrderIconWrap>
        )}
        {children}
        {showSetTargetPriceAgain && (
          <ButtonWrap>
            <ModalButtons
              isVertical
              showOnlyPrimary
              primaryButtonProps={{
                onClick: onSetTargetPriceAgain,
                label: t(
                  'portfolio.pendingTransactions.details.setTargetPriceAgain',
                ),
                'data-cy': 'button-set-target-price-again',
              }}
            />
          </ButtonWrap>
        )}
      </SectionLoader>
    </BasicModal>
  );
};

TriggerOrderModalFrame.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  imageUrl: PropTypes.string,
  showSetTargetPriceAgain: PropTypes.bool,
  onSetTargetPriceAgain: PropTypes.func,
  isLoading: PropTypes.bool,
  children: PropTypes.node,
};

// A row the BE words itself: the label is the field's CustomCaption and the text
// its Value. It arrives with caption code 41, which the caption list names after
// something else here, so it is told from the Target price field, and from every
// other one, by its format (16) alone.
const isCustomCaptionField = (field) => field.FormatType === 'CustomCaption';

// The field with this caption, never the custom row that shares the Target
// price's.
const findFieldByCaption = (fields, caption) =>
  fields.find(
    (field) => field.CaptionType === caption && !isCustomCaptionField(field),
  );

// The label of each row the details show, by its Field's caption. A Field whose
// caption isn't here, like the product, has no row of its own.
const ROW_LABEL_KEYS = {
  Summary: 'portfolio.pendingTransactions.details.summaryLabel',
  TargetPrice: 'portfolio.pendingTransactions.details.targetPrice',
  PricePerOz: 'portfolio.pendingTransactions.details.pricePerOz',
  PricePerUnit: 'portfolio.pendingTransactions.details.pricePerUnit',
  Status: 'portfolio.pendingTransactions.tableHeadings.status',
  SubmittedOn: 'portfolio.pendingTransactions.details.submittedOn',
  TransactedOn: 'portfolio.pendingTransactions.details.cancelledOn',
  Submitter: 'portfolio.pendingTransactions.details.submitter',
  TargetPriceId: 'portfolio.pendingTransactions.details.referenceNumber',
  ReferenceNumber: 'portfolio.pendingTransactions.details.referenceNumber',
};

// The Fields that make a row of the details, in the order the BE sends them,
// which is the order they are shown in: the BE's rows, custom ones included, are
// never moved. A canceled order carries its reference as a ReferenceNumber
// field rather than a TargetPriceId one, so that one is left out when the order
// has a Target price ID.
const getRowFields = (fields) => {
  const hasTargetPriceId = fields.some(
    (field) =>
      field.CaptionType === 'TargetPriceId' && !isCustomCaptionField(field),
  );

  return fields.filter(
    (field) =>
      isCustomCaptionField(field) ||
      (ROW_LABEL_KEYS[field.CaptionType] &&
        !(hasTargetPriceId && field.CaptionType === 'ReferenceNumber')),
  );
};

// The details' rows, one per Field and in the Fields' order. `status` is the
// order's, shown by its Status row; an Expired one words its date row as such.
const FieldRows = ({ fields, status }) => {
  const { t } = useTranslation();

  const renderValue = (field) => {
    switch (field.FormatType) {
      case 'TransactionSummary':
        return t('portfolio.pendingTransactions.triggerOrderLabel', {
          quantity: parseFloat(field.AmountOrQuantity).toFixed(field.Precision),
          unit: t(
            field.AmountOrQuantity > 1
              ? 'portfolio.transactions.details.pluralUnit'
              : 'portfolio.transactions.details.singleUnit',
          ),
        });

      case 'Currency':
        return formatAbsolutMoneyNumeral(field.Amount);

      case 'TransactionStatus':
        return <Status status={status} isTargetPriceOrder />;

      case 'DateTime':
        return formatDate(field.DateTime, DATE_FORMAT);

      case 'CustomCaption':
        return field.Value;

      default:
        return field.Caption;
    }
  };

  const renderLabel = (field) => {
    if (isCustomCaptionField(field)) {
      return field.CustomCaption;
    }

    return t(
      field.CaptionType === 'TransactedOn' &&
        status === transactionStatusTypes.Expired
        ? 'portfolio.pendingTransactions.details.expiredOn'
        : ROW_LABEL_KEYS[field.CaptionType],
    );
  };

  return fields.map((field) => (
    <TransactionDetailRow key={field.CustomCaption || field.CaptionType}>
      <FieldRowLabel>{renderLabel(field)}</FieldRowLabel>
      <FieldRowValue>{renderValue(field)}</FieldRowValue>
    </TransactionDetailRow>
  ));
};

FieldRows.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      CaptionType: PropTypes.string,
      FormatType: PropTypes.string,
      CustomCaption: PropTypes.string,
      Value: PropTypes.string,
    }),
  ),
  status: PropTypes.string,
};

// The prices the BE didn't send, worked out from the target price (see above).
const getDerivedPriceFields = (fields, productCaption) => {
  const targetPrice = findFieldByCaption(fields, 'TargetPrice')?.Amount;

  if (!(targetPrice > 0)) {
    return [];
  }

  const ouncesPerUnit = getOuncesPerUnit(productCaption);

  return [
    ouncesPerUnit && {
      CaptionType: 'PricePerOz',
      FormatType: 'Currency',
      Amount: targetPrice / ouncesPerUnit,
    },
    {
      CaptionType: 'PricePerUnit',
      FormatType: 'Currency',
      Amount: targetPrice,
    },
  ].filter((field) => field && !findFieldByCaption(fields, field.CaptionType));
};

// The status of a canceled or expired order, from its Status Field.
const getEndedOrderStatus = (fields) =>
  parseEnumType(
    transactionStatusTypesArray,
    findFieldByCaption(fields, 'Status')?.TransactionStatusType,
  );

// A canceled or expired order's details arrive as the transaction's Fields, and
// are shown in the order the BE sends them.
const EndedOrderDetails = ({ transaction }) => {
  const brokerSettings = useSelector(selectSettings);
  const fields = transaction.Fields || [];
  const productField = fields.find((field) => field.FormatType === 'Product');
  const status = getEndedOrderStatus(fields);
  // The prices the BE didn't send go right after the target price they are
  // worked out from.
  const derivedPriceFields = getDerivedPriceFields(
    fields,
    productField?.ProductCaption,
  );
  const rowFields = getRowFields(fields).reduce(
    (rows, field) => [
      ...rows,
      field,
      ...(field.CaptionType === 'TargetPrice' && !isCustomCaptionField(field)
        ? derivedPriceFields
        : []),
    ],
    [],
  );

  return (
    <>
      {productField && (
        <ProductField
          field={productField}
          isSegregated={transaction.IsSegregated}
          segregatedCaption={brokerSettings?.SegregatedCaption}
        />
      )}
      <DividerField />
      <FieldRows fields={rowFields} status={status} />
    </>
  );
};

EndedOrderDetails.propTypes = {
  transaction: PropTypes.shape({
    IsSegregated: PropTypes.bool,
    Fields: PropTypes.arrayOf(
      PropTypes.shape({
        CaptionType: PropTypes.string,
        FormatType: PropTypes.string,
        CustomCaption: PropTypes.string,
        Value: PropTypes.string,
      }),
    ),
  }),
};

const TriggerOrderDetailsModal = ({
  isOpen,
  onClose,
  transaction,
  isEnded,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brokerSettings = useSelector(selectSettings);
  const accountUsers = useSelector(selectAccountUsersData);
  const { isTriggerOrderEntryHidden } = useTriggerOrderVerification();
  const targetPriceAgain = useSelector(selectTargetPriceAgain);
  // When this modal made its Set target price again request, which tells it from
  // any other the store has held, so only the modal whose button was pressed
  // spins and closes.
  const [requestedAt, setRequestedAt] = useState(null);
  const ownRequest =
    requestedAt !== null && targetPriceAgain?.requestedAt === requestedAt
      ? targetPriceAgain
      : null;
  const isRequestPending =
    ownRequest?.status === TARGET_PRICE_AGAIN_STATUS.PENDING;

  // The request has ended: close with it when the Target Price flow is up (or
  // the account was blocked with the popup instead), but stay open, button
  // usable again, when nothing could be opened.
  useEffect(() => {
    if (!ownRequest || isRequestPending) return;

    setRequestedAt(null);

    if (isOpen && ownRequest.status !== TARGET_PRICE_AGAIN_STATUS.DROPPED) {
      onClose();
    }
  }, [ownRequest, isRequestPending, isOpen, onClose]);

  if (isEmpty(transaction)) {
    return null;
  }

  const imageUrl = getProductImageUrl(transaction, brokerSettings?.RootUrl);
  // Set target price again is another way into creating a Target Price Order,
  // so it follows the rules of the others: only while the feature is on, and
  // not for an account that has no Target Price Order entry at all. It also
  // needs the order's product to open the flow for.
  const productCode = getProductCode(transaction);
  const canSetTargetPriceAgain =
    !!productCode &&
    !!brokerSettings?.IsTriggerOrderActive &&
    !isTriggerOrderEntryHidden;
  // Asks for the Target Price flow to open, on this same page, on Order Entry for
  // the order's product and vault (the Header hosts the flow, see
  // useTargetPriceAgain). The details stay open, spinning, until it is up, and
  // close then (see the effect above).
  const handleSetTargetPriceAgain = () => {
    if (isRequestPending) return;

    const request = requestTargetPriceAgain({
      productCode,
      ...getOrderVault(transaction),
    });

    setRequestedAt(request.payload.requestedAt);
    dispatch(request);
  };

  if (isEnded) {
    return (
      <TriggerOrderModalFrame
        isOpen={isOpen}
        onClose={onClose}
        title={t('portfolio.pendingTransactions.details.transactionTitle')}
        imageUrl={imageUrl}
        showSetTargetPriceAgain={
          canSetTargetPriceAgain &&
          getEndedOrderStatus(transaction.Fields || []) ===
            transactionStatusTypes.Expired
        }
        onSetTargetPriceAgain={handleSetTargetPriceAgain}
        isLoading={isRequestPending}
      >
        <EndedOrderDetails transaction={transaction} />
      </TriggerOrderModalFrame>
    );
  }

  // The submitter's name is the order's Submitter field (caption 28). Without
  // one (the details couldn't be loaded, or the BE didn't send it) the
  // transaction-requests response only carries the submitter's UID, so resolve
  // it to a name from the account's users. A user who is no longer on the
  // account isn't in that list, so fall back to the raw UID.
  const submitter = accountUsers?.find(
    (user) => user.UserUid === transaction.SubmitterUid,
  );

  // SuffixType is a 1-based index into userSuffixTypes when numeric (as written
  // by EditFullName); the profile screens render it already as a string.
  const submitterSuffix =
    typeof submitter?.SuffixType === 'number'
      ? parseEnumType(userSuffixTypes, submitter.SuffixType)
      : submitter?.SuffixType;
  const submitterName = [
    submitter?.FirstName,
    submitter?.MiddleName,
    submitter?.LastName,
    submitterSuffix,
  ]
    .filter(Boolean)
    .join(' ');

  const unit = t(
    transaction.Quantity > 1
      ? 'portfolio.transactions.details.pluralUnit'
      : 'portfolio.transactions.details.singleUnit',
  );

  const status = parseEnumType(
    triggerOrderStatusTypesArray,
    transaction.TriggerOrderStatus,
  );

  // The rest of the details are the order's Fields, which come from the single
  // transaction API (already normalized by the saga), shown in the order the BE
  // sends them. Summary is the transaction-requests row's own, so its Field is
  // left out, and a Submitter Field with no name is left to the fallback below.
  const detailFields = getRowFields(transaction.Fields || []).filter(
    (field) =>
      field.CaptionType !== 'Summary' &&
      (field.CaptionType !== 'Submitter' || field.Caption),
  );
  const hasField = (caption) =>
    detailFields.some((field) => field.CaptionType === caption);
  // An order whose details couldn't be loaded has no Fields, so its status and
  // submitter, which the row carries too, are shown from the row instead.
  const rowFields = [
    ...detailFields,
    ...(hasField('Status')
      ? []
      : [{ CaptionType: 'Status', FormatType: 'TransactionStatus' }]),
    ...(hasField('Submitter')
      ? []
      : [
          {
            CaptionType: 'Submitter',
            FormatType: 'String',
            Caption: submitterName || transaction.SubmitterUid,
          },
        ]),
  ];

  return (
    <TriggerOrderModalFrame
      isOpen={isOpen}
      onClose={onClose}
      title={t('portfolio.pendingTransactions.details.title')}
      imageUrl={imageUrl}
      showSetTargetPriceAgain={
        canSetTargetPriceAgain && status === transactionStatusTypes.Expired
      }
      onSetTargetPriceAgain={handleSetTargetPriceAgain}
      isLoading={isRequestPending}
    >
      <ProductField
        field={transaction}
        isSegregated={transaction.IsSegregated}
        segregatedCaption={brokerSettings?.SegregatedCaption}
      />
      <DividerField />
      <TransactionDetailRow>
        <TransactionDetailLabel>
          {t('portfolio.pendingTransactions.details.summaryLabel')}
        </TransactionDetailLabel>
        <TransactionDetailValue>
          {t('portfolio.pendingTransactions.details.summary', {
            quantity: transaction.Quantity,
            unit,
          })}
        </TransactionDetailValue>
      </TransactionDetailRow>
      <FieldRows fields={rowFields} status={status} />
    </TriggerOrderModalFrame>
  );
};

TriggerOrderDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  // A canceled or expired order, whose details arrive as the transaction's
  // Fields rather than as a transaction-requests row.
  isEnded: PropTypes.bool,
  transaction: PropTypes.shape({
    ProductCaption: PropTypes.string,
    ProductCode: PropTypes.string,
    LocationType: PropTypes.number,
    IsSegregated: PropTypes.bool,
    SubmitterUid: PropTypes.string,
    Quantity: PropTypes.number,
    TriggerOrderStatus: PropTypes.number,
    Fields: PropTypes.arrayOf(
      PropTypes.shape({
        CaptionType: PropTypes.string,
        FormatType: PropTypes.string,
        CustomCaption: PropTypes.string,
        Value: PropTypes.string,
      }),
    ),
  }),
};

export default TriggerOrderDetailsModal;
